import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowUpRight, ArrowRight, Instagram, MapPin } from "lucide-react";

export default function Home() {
  const [projects, setProjects] = useState([]);
  const [services, setServices] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [settings, setSettings] = useState({});

  useEffect(() => {
    api.get("/projects?sort=featured&limit=8").then((r) => setProjects(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/testimonials?featured=true").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/site-settings").then((r) => setSettings(r.data)).catch(() => {});
  }, []);

  const featured = projects;

  return (
    <div data-testid="page-home" className="bg-[#F9F8F6] text-[#2B2927]">
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-end overflow-hidden noise" data-testid="hero-section">
        <img
          src="/projects/girish-dhakan/01.jpg"
          alt="Hiraya — Girish Dhakan Residence, Surat"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/80 via-[#1A1918]/20 to-transparent" />
        <div className="relative container-editorial pb-16 md:pb-24 pt-32 md:pt-40 w-full">
          <div className="max-w-4xl fade-up">
            <div className="text-[11px] uppercase tracking-[0.36em] text-[#EAE6DF]/90 mb-6">Interior Design Studio · Surat · Since 2017</div>
            <h1 className="font-serif text-[#F9F8F6] text-5xl md:text-7xl lg:text-[92px] leading-[0.95] tracking-tight text-balance">
              Where <em className="not-italic text-[#EFC9B7]">function</em> meets finesse.
            </h1>
            <p className="mt-8 text-[#EAE6DF] max-w-2xl text-base md:text-lg leading-relaxed">
              Hiraya (formerly Adorn Designs) crafts residential and commercial interiors across Gujarat, Rajasthan and Karnataka — spaces designed around utility, warmth, and the way you actually live.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/consultation" data-testid="hero-cta-book" className="inline-flex items-center gap-2 px-7 py-4 bg-[#F9F8F6] text-[#1A1918] text-xs uppercase tracking-[0.22em] font-medium rounded-sm hover:bg-[#A85C42] hover:text-white transition-colors">
                Book a Consultation <ArrowRight size={14} />
              </Link>
              <Link to="/portfolio" data-testid="hero-cta-projects" className="inline-flex items-center gap-2 px-7 py-4 border border-[#F9F8F6]/70 text-[#F9F8F6] text-xs uppercase tracking-[0.22em] font-medium rounded-sm hover:bg-[#F9F8F6] hover:text-[#1A1918] transition-colors">
                View Projects
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="container-editorial py-24 md:py-32" data-testid="intro-section">
        <div className="grid grid-cols-12 gap-8 items-start">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42]">The studio</div>
          </div>
          <div className="col-span-12 md:col-span-8">
            <h2 className="font-serif text-3xl md:text-5xl leading-[1.1] tracking-tight text-balance">
              <em className="not-italic text-[#A85C42]">Hiraya</em> — a Filipino word meaning the fruit of one's hopes, dreams and aspirations. Formerly known as <em className="not-italic">Adorn Designs</em>, we've quietly built purposeful interiors from Surat since 2017.
            </h2>
            <p className="mt-8 max-w-2xl text-[#4A4642] leading-relaxed">
              Led by Surabhi — Principal Designer, Founder and CEO — with 13 years of industry experience across Surat, Vapi, Bardoli, Valsad, Kishangarh, Ajmer, Bangalore, and a consultation stint in Dublin.
            </p>
          </div>
        </div>
      </section>

      {/* Featured projects — asymmetric grid */}
      <section className="container-editorial py-16 md:py-24" data-testid="featured-projects">
        <div className="flex items-end justify-between mb-12 md:mb-16 gap-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">Selected work</div>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight">Recent projects</h2>
          </div>
          <Link to="/portfolio" data-testid="featured-view-all" className="inline-flex items-center gap-2 text-sm font-medium border-b-2 border-[#1A1918] pb-1 hover:text-[#A85C42] hover:border-[#A85C42] transition">
            View all projects <ArrowUpRight size={16} />
          </Link>
        </div>

        {featured.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {featured.slice(0, 4).map((p, i) => (
              <Link key={p.id} to={`/portfolio/${p.slug}`} data-testid={`featured-project-${i}`} className="group relative overflow-hidden rounded-sm aspect-[4/5]">
                <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1A1918]/90 via-[#1A1918]/20 to-transparent" />
                <div className="absolute inset-0 p-6 md:p-8 flex flex-col justify-end text-[#F9F8F6]">
                  <div className="text-[10px] uppercase tracking-[0.28em] text-[#EAE6DF]/90">{p.location} · {p.year}</div>
                  <div className="font-serif text-2xl md:text-3xl mt-2 tracking-tight">{p.title}</div>
                </div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-[#7A736C] py-16 text-center">Loading projects…</div>
        )}
      </section>

      {/* Services */}
      <section className="bg-[#EAE6DF] py-24 md:py-32 relative noise" data-testid="services-section">
        <div className="container-editorial">
          <div className="grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">What we do</div>
              <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-none">A calm, single-team practice.</h2>
              <p className="mt-6 text-[#4A4642] max-w-md">
                From single rooms to full turnkey homes and commercial spaces — one design team from first sketch to final styling. Utility first, always.
              </p>
              <Link to="/services" className="inline-flex items-center gap-2 mt-8 text-sm font-medium border-b-2 border-[#1A1918] pb-1 hover:text-[#A85C42] hover:border-[#A85C42]">
                Explore services <ArrowUpRight size={14} />
              </Link>
            </div>
            <div className="col-span-12 md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-px bg-[#C7C1B7]">
              {services.slice(0, 6).map((s, i) => (
                <Link key={s.id} to={`/services/${s.slug}`} data-testid={`service-card-${i}`} className="group bg-[#EAE6DF] p-8 hover:bg-[#F9F8F6] transition-colors">
                  <div className="flex items-start justify-between">
                    <div className="text-xs uppercase tracking-[0.24em] text-[#7A736C]">0{i + 1}</div>
                    <ArrowUpRight size={16} className="opacity-40 group-hover:opacity-100 group-hover:text-[#A85C42] transition" />
                  </div>
                  <h3 className="font-serif text-2xl md:text-3xl mt-6 tracking-tight leading-tight">{s.title}</h3>
                  <p className="mt-4 text-sm text-[#4A4642] leading-relaxed">{s.short_description}</p>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="container-editorial py-24 md:py-32 overflow-hidden" data-testid="philosophy-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <img src="/projects/sunil-barolia/02.jpg" alt="Design detail" className="w-full aspect-[4/5] object-cover rounded-sm" />
          </div>
          <div className="md:col-span-6 md:col-start-7">
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">Design philosophy</div>
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight leading-[1.02]">
              Utility is the primary <em className="not-italic text-[#A85C42]">responsibility</em> of a designer.
            </h2>
            <p className="mt-8 text-[#4A4642] leading-relaxed">
              We believe optimising space efficiently is the foundation of great design. Aesthetics is the reward that follows. Every detail earns its place — through material honesty, patient work, and a deep respect for how you actually live.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[#4A4642]">
              <li className="flex gap-4 items-start"><span className="text-[#A85C42] font-serif text-xl leading-none">·</span> Utility first — layouts, storage, flow before finish.</li>
              <li className="flex gap-4 items-start"><span className="text-[#A85C42] font-serif text-xl leading-none">·</span> Inspired by nature — palette, texture, ageing gracefully.</li>
              <li className="flex gap-4 items-start"><span className="text-[#A85C42] font-serif text-xl leading-none">·</span> Collaboration — your tastes blended in, never over-written.</li>
              <li className="flex gap-4 items-start"><span className="text-[#A85C42] font-serif text-xl leading-none">·</span> Tailor-made — no templated interiors, ever.</li>
            </ul>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="bg-[#2B2927] text-[#F9F8F6] py-24" data-testid="process-section">
        <div className="container-editorial">
          <div className="flex items-end justify-between mb-14 gap-6 flex-wrap">
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">The Hiraya Design Journey</h2>
            <Link to="/process" className="text-xs font-medium uppercase tracking-[0.24em] border-b-2 border-[#F9F8F6]/70 pb-1 hover:text-[#EFC9B7] hover:border-[#EFC9B7]">Read the full 9 stages</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-x-6 gap-y-8">
            {["Discover", "Plan", "Concept", "Bespoke", "3D", "Materials", "Budget", "Detail", "Style"].slice(0, 6).map((s, i) => (
              <div key={s} data-testid={`process-step-${i}`} className="border-t border-[#3A3734] pt-6">
                <div className="text-xs text-[#EFC9B7] mb-3">0{i + 1}</div>
                <div className="font-serif text-xl md:text-2xl tracking-tight">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="container-editorial py-24 md:py-32" data-testid="testimonials-section">
          <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">In their words · via Houzz</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight mb-14 max-w-3xl">Reviewed by the clients we've built for.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {testimonials.slice(0, 3).map((t, i) => (
              <blockquote key={t.id} data-testid={`testimonial-${i}`} className="border-t border-[#D5D0C8] pt-8">
                <div className="text-[#A85C42] text-lg mb-2">★★★★★</div>
                <div className="font-serif text-xl md:text-[24px] leading-snug text-[#2B2927] italic">"{t.quote}"</div>
                <div className="mt-6 text-sm">
                  <div className="text-[#2B2927]">{t.author}</div>
                  <div className="text-[#7A736C]">{t.role}{t.location ? ` · ${t.location}` : ""}</div>
                </div>
              </blockquote>
            ))}
          </div>
        </section>
      )}

      {/* Service areas */}
      <section className="bg-[#F2EFE9] py-20 relative noise">
        <div className="container-editorial">
          <div className="grid grid-cols-12 gap-8 items-start">
            <div className="col-span-12 md:col-span-4">
              <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">Service areas</div>
              <h2 className="font-serif text-3xl md:text-5xl tracking-tight leading-tight">Where we work.</h2>
            </div>
            <div className="col-span-12 md:col-span-8">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                {[
                  { region: "Gujarat", cities: ["Surat", "Vapi", "Bardoli", "Valsad"] },
                  { region: "Rajasthan", cities: ["Kishangarh", "Jaipur", "Ajmer"] },
                  { region: "Karnataka", cities: ["Bangalore"] },
                ].map((r) => (
                  <div key={r.region} className="border-t border-[#C7C1B7] pt-4">
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#A85C42] mb-3">{r.region}</div>
                    <ul className="space-y-2 text-[#2B2927]">
                      {r.cities.map((c) => <li key={c} className="flex items-center gap-2 text-sm"><MapPin size={12} strokeWidth={1.5} /> {c}</li>)}
                    </ul>
                  </div>
                ))}
              </div>
              <p className="mt-8 text-sm text-[#4A4642]">Consultation service also available further afield — including a previous engagement in Dublin, Ireland.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Instagram section */}
      <section className="py-24 overflow-hidden" data-testid="instagram-section">
        <div className="container-editorial flex items-end justify-between mb-10 gap-6 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.32em] text-[#A85C42] mb-4">Follow the studio</div>
            <h2 className="font-serif text-3xl md:text-5xl tracking-tight">@thehiraya_</h2>
          </div>
          <a href="https://www.instagram.com/thehiraya_" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-medium border-b-2 border-[#1A1918] pb-1 hover:text-[#A85C42] hover:border-[#A85C42]">
            <Instagram size={14} /> Visit Instagram
          </a>
        </div>
        {featured.length > 0 && (
          <div className="relative">
            <div className="flex gap-4 marquee-track" style={{ width: "max-content" }}>
              {[...featured, ...featured].map((p, i) => (
                <div key={`m-${i}`} className="w-[240px] md:w-[320px] aspect-square shrink-0">
                  <img src={p.cover_image} alt="" className="w-full h-full object-cover rounded-sm" />
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Final CTA */}
      <section className="container-editorial pb-24 md:pb-32" data-testid="final-cta">
        <div className="bg-[#A85C42] text-[#F9F8F6] p-12 md:p-20 rounded-sm noise relative overflow-hidden">
          <div className="relative">
            <h2 className="font-serif text-4xl md:text-6xl tracking-tight text-balance max-w-3xl leading-[1.02]">
              Ready to shape a space around the way you actually live?
            </h2>
            <p className="mt-6 max-w-xl text-[#F9F8F6]/90">Book a consultation with Surabhi and the Hiraya team — in Surat, or over a video call anywhere.</p>
            <Link to="/consultation" data-testid="final-cta-book" className="inline-flex items-center gap-3 mt-10 px-8 py-4 bg-[#F9F8F6] text-[#1A1918] text-xs font-medium uppercase tracking-[0.24em] rounded-sm hover:bg-[#1A1918] hover:text-[#F9F8F6] transition-colors">
              Book a consultation <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
